// ── A/P aging insights ──────────────────────────────────────────────────────
//
// Says when the A/P aging mix is shifting out — payables being stretched, or
// stretched further — for the "What changed" line on the A/P Aging Trend card
// (Collateral dashboard and certificate page).
//
// THE SAME TWO RULES AS INSIGHTS. dbo.vChecksAPMix
// (sql/2026-11-03_insights_ap_aging_mix.sql) is the Insights version; this is
// its mirror for the card, and it reads the SAME thresholds
// (getInsightThresholds → "AP aging shift", "AP aging drift"), falling back to
// the seeded defaults when they cannot be read. Change a rule in one place,
// change it in the other.
//
//   shift   share of A/P more than 30 days past due, latest month vs the
//           average of the (up to) three months before. Needs 2+ prior months.
//   drift   consecutive months that share has risen, each by at least half a
//           point; flags only when the total rise is at least 5 points.
//
// One certificate per month (the last one in it), so weekly and monthly
// reporters compare on the same clock. A/P under $50,000 is not scored.

export const AP_DEFAULTS = {
  shift: { medium: 0.10, high: 0.20, on: true },   // percentage POINTS, as fractions
  drift: { medium: 3,    high: 5,    on: true },   // months in a row
};
const FLOOR = 50000;      // open A/P below this is not scored
const STEP = 0.005;       // a month must rise at least half a point to count
const MIN_RISE = 0.05;    // a drift must add at least 5 points in total

// getInsightThresholds rows → the two rules (defaults for anything missing).
export const apThresholds = (rows) => {
  const out = { shift: { ...AP_DEFAULTS.shift }, drift: { ...AP_DEFAULTS.drift } };
  (Array.isArray(rows) ? rows : []).forEach(r => {
    const k = r.metric === "AP aging shift" ? "shift" : r.metric === "AP aging drift" ? "drift" : null;
    if (!k) return;
    if (r.medium_value != null) out[k].medium = Number(r.medium_value);
    if (r.high_value != null) out[k].high = Number(r.high_value);
    out[k].on = r.isactive === undefined ? true : !!r.isactive;
  });
  return out;
};

const num = v => (v == null || v === "" ? 0 : (parseFloat(v) || 0));
const d10 = d => String(d || "").slice(0, 10);
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const monLabel = ym => MON[parseInt(ym.slice(5, 7), 10) - 1] + " " + ym.slice(0, 4);   // "Sep 2026" — "Sep 26" reads as a day
const p1 = x => (x * 100).toFixed(1) + "%";
const pts = x => (x >= 0 ? "+" : "") + (x * 100).toFixed(1) + " pts";

/**
 * @param hist   certificates (getBorrowingBases rows), any order
 * @param asOf   optional YYYY-MM-DD: judge as of this certificate date
 * @param th     apThresholds(...) — defaults when omitted
 * @returns {{findings: Array<{key,severity,title,detail}>, series: Array}}
 *          severity "High" | "Medium"; series is the monthly share, oldest first
 */
export function apAgingInsights(hist, asOf, th = AP_DEFAULTS) {
  const rows = (hist || [])
    .map(r => {
      const b = [r.ap_dd_current, r.ap_dd_1_30, r.ap_dd_31_60, r.ap_dd_61_90, r.ap_dd_over90].map(num);
      const total = b.reduce((a, x) => a + x, 0);
      return { id: r.id, d: d10(r.asofdate), total, s31: total ? (b[2] + b[3] + b[4]) / total : 0, s90: total ? b[4] / total : 0 };
    })
    .filter(r => r.d && r.total > 0 && (!asOf || r.d <= d10(asOf)));

  // Last certificate in each month (date desc, then id desc — as the SQL).
  const byMo = new Map();
  rows.forEach(r => {
    const ym = r.d.slice(0, 7), cur = byMo.get(ym);
    if (!cur || r.d > cur.d || (r.d === cur.d && Number(r.id) > Number(cur.id))) byMo.set(ym, { ...r, ym });
  });
  const m = [...byMo.values()].sort((a, b) => (a.ym < b.ym ? 1 : -1));   // newest first
  const series = m.slice().reverse();
  const findings = [];
  if (!m.length) return { findings, series };

  const L = m[0];
  const small = L.total < FLOOR;

  // ── shift ──
  const base = m.slice(1, 4);
  if (th.shift.on && base.length >= 2 && !small) {
    const avg = base.reduce((a, x) => a + x.s31, 0) / base.length;
    const shift = L.s31 - avg;
    const sev = shift >= th.shift.high ? "High" : shift >= th.shift.medium ? "Medium" : null;
    if (sev) {
      const from = base[base.length - 1].ym, to = base[0].ym;
      findings.push({
        key: "ap-shift", severity: sev,
        title: `A/P past 30 days ${pts(shift)}`,
        detail: `${p1(L.s31)} of open A/P is more than 30 days past due at ${monLabel(L.ym)}, up from ${p1(avg)} on average over ${monLabel(from)}${from === to ? "" : "–" + monLabel(to)}. Over 90 days: ${p1(L.s90)}.`,
      });
    }
  }

  // ── drift ──
  if (th.drift.on && m.length >= 2 && !small) {
    let run = 0;
    while (run + 1 < m.length && m[run].s31 >= m[run + 1].s31 + STEP) run++;
    const start = m[run];
    const rise = L.s31 - start.s31;
    const sev = rise < MIN_RISE ? null : run >= th.drift.high ? "High" : run >= th.drift.medium ? "Medium" : null;
    if (sev) {
      findings.push({
        key: "ap-drift", severity: sev,
        title: `Stretching ${run} months running`,
        detail: `The share of A/P more than 30 days past due has risen ${run} months in a row, from ${p1(start.s31)} at ${monLabel(start.ym)} to ${p1(L.s31)} at ${monLabel(L.ym)} — payables are being stretched further each month.`,
      });
    }
  }

  findings.sort((a, b) => (a.severity === b.severity ? 0 : a.severity === "High" ? -1 : 1));
  return { findings, series };
}
